// Ordinary-least-squares fit of y on x, used to draw the regression line and
// annotate the correlation scatter. Pure numeric helper — no charting deps.
export interface OlsFit {
  slope: number;
  intercept: number;
  // Coefficient of determination R^2 in [0, 1]. For a simple linear regression
  // this equals the squared Pearson correlation.
  r2: number;
}

// Returns null when there are fewer than 2 points or x has zero variance (a
// vertical fit — slope is undefined). When y has zero variance the line is
// flat and fits every point exactly, so R^2 is reported as 1.
export const olsFit = (xs: number[], ys: number[]): OlsFit | null => {
  const n = xs.length;
  if (n < 2 || ys.length !== n) return null;

  let sumX = 0;
  let sumY = 0;
  for (let i = 0; i < n; i++) {
    sumX += xs[i];
    sumY += ys[i];
  }
  const meanX = sumX / n;
  const meanY = sumY / n;

  let sxx = 0;
  let syy = 0;
  let sxy = 0;
  for (let i = 0; i < n; i++) {
    const dx = xs[i] - meanX;
    const dy = ys[i] - meanY;
    sxx += dx * dx;
    syy += dy * dy;
    sxy += dx * dy;
  }

  if (sxx === 0) return null;

  const slope = sxy / sxx;
  const intercept = meanY - slope * meanX;
  // Clamp: floating-point can nudge a near-perfect fit a hair past 1.0.
  const r2 = syy === 0 ? 1 : Math.min(1, (sxy * sxy) / (sxx * syy));

  return { slope, intercept, r2 };
};
